export interface RecordedChunk {
  blob: Blob
  durationMs: number
  index: number
  createdAt: string
  isFinal: boolean
}

export interface MediaRecorderSessionOptions {
  stream: MediaStream
  mimeType?: string
  bitrate?: number
  // Shorter chunks = smaller loss window if the OS kills the tab mid-chunk
  // (iOS/Android backgrounding, phone calls).
  chunkDurationMs?: number
  // Minimum elapsed before a visibility-hide flush emits a chunk,
  // to avoid near-empty blobs from rapid hide/show cycles.
  minFlushElapsedMs?: number
  onChunk?: (chunk: RecordedChunk) => Promise<void> | void
  // Fired once if a full segment ends before any audio bytes have arrived.
  // iOS Safari emits no data at all while the mic track is muted (e.g. a call
  // or another capture holds the microphone), so the recording would be empty.
  onNoAudio?: () => void
  onRecordingStopped: (payload: {
    blob: Blob
    durationMs: number
    chunks: RecordedChunk[]
  }) => void
  onError?: (error: Event) => void
}

const DEFAULT_CHUNK_DURATION_MS = 3_000
const DEFAULT_MIN_FLUSH_ELAPSED_MS = 200

export class MediaRecorderSession {
  private readonly recorder: MediaRecorder
  private readonly actualMimeType: string | undefined
  private readonly options: MediaRecorderSessionOptions
  private readonly chunkDurationMs: number
  private readonly minFlushElapsedMs: number
  private chunks: RecordedChunk[] = []
  private currentChunkData: Blob[] = []
  private chunkIndex = 0
  private chunkStartTime = 0
  private chunkTimeout: number | null = null
  private finalStopRequested = false
  private noAudioReported = false
  private startTime = 0

  constructor(options: MediaRecorderSessionOptions) {
    this.options = options
    this.chunkDurationMs = options.chunkDurationMs ?? DEFAULT_CHUNK_DURATION_MS
    this.minFlushElapsedMs = options.minFlushElapsedMs ?? DEFAULT_MIN_FLUSH_ELAPSED_MS

    let recorder: MediaRecorder
    let mimeType = options.mimeType
    const bitrate = options.bitrate ?? 128_000
    try {
      recorder = new MediaRecorder(options.stream, {
        audioBitsPerSecond: bitrate,
        mimeType,
      })
    } catch (error) {
      // Safari sometimes requires no mimeType to be specified
      console.warn(
        'Failed to create MediaRecorder with specified mimeType, trying default:',
        error,
      )
      recorder = new MediaRecorder(options.stream, {
        audioBitsPerSecond: bitrate,
      })
      mimeType = recorder.mimeType || undefined
    }
    this.recorder = recorder
    this.actualMimeType = mimeType
    this.wireHandlers()
  }

  start(): void {
    this.currentChunkData = []
    this.chunkStartTime = Date.now()
    this.startTime = Date.now()
    this.startChunkTimer()
    this.recorder.start(100)
  }

  stop(): void {
    this.finalStopRequested = true
    if (this.chunkTimeout) {
      clearTimeout(this.chunkTimeout)
      this.chunkTimeout = null
    }
    this.recorder.stop()
  }

  pause(): void {
    this.recorder.pause()
  }

  resume(): void {
    this.recorder.resume()
    if (this.chunkTimeout) {
      const elapsed = Date.now() - this.chunkStartTime
      const remaining = Math.max(this.chunkDurationMs - elapsed, 1000)
      clearTimeout(this.chunkTimeout)
      this.chunkTimeout = window.setTimeout(() => {
        if (!this.finalStopRequested && this.recorder.state === 'recording') {
          this.recorder.stop()
        }
      }, remaining)
    }
  }

  // When the tab is hidden, force-flush the current chunk to disk so the OS
  // can suspend JS without losing audio in memory.
  persistChunkNow(): void {
    if (this.recorder.state !== 'recording') return
    if (this.finalStopRequested) return
    if (Date.now() - this.chunkStartTime < this.minFlushElapsedMs) return
    if (this.chunkTimeout) {
      clearTimeout(this.chunkTimeout)
      this.chunkTimeout = null
    }
    try {
      this.recorder.stop()
    } catch (err) {
      console.warn('[recorder] stop on suspend failed', err)
    }
  }

  get state(): RecordingState {
    return this.recorder.state
  }

  get mimeType(): string | undefined {
    return this.actualMimeType
  }

  get sessionStartTime(): number {
    return this.startTime
  }

  private wireHandlers(): void {
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.currentChunkData.push(event.data)
      }
    }

    this.recorder.onstop = async () => {
      const isFinal = this.finalStopRequested
      await this.finalizeChunk(isFinal)

      if (!isFinal) {
        if (this.chunks.length === 0 && !this.noAudioReported) {
          this.noAudioReported = true
          this.options.onNoAudio?.()
        }
        // Start next chunk without ending the session
        this.currentChunkData = []
        this.chunkStartTime = Date.now()
        this.startChunkTimer()
        this.recorder.start(100)
        return
      }

      if (this.chunkTimeout) {
        clearTimeout(this.chunkTimeout)
        this.chunkTimeout = null
      }

      const fallbackType = this.actualMimeType || this.recorder.mimeType || 'audio/webm'
      let stitchedBlob: Blob
      if (this.chunks.length === 0) {
        console.warn('No chunks available after recording stop')
        stitchedBlob = new Blob([], { type: fallbackType })
      } else {
        stitchedBlob = new Blob(
          this.chunks.map((chunk) => chunk.blob),
          { type: fallbackType },
        )
      }

      const duration = this.chunks.reduce((sum, chunk) => sum + chunk.durationMs, 0)

      this.options.onRecordingStopped({
        blob: stitchedBlob,
        durationMs: duration || Date.now() - this.startTime,
        chunks: this.chunks,
      })
    }

    this.recorder.onerror = (event) => {
      this.options.onError?.(event)
    }
  }

  private async finalizeChunk(isFinal: boolean): Promise<void> {
    if (this.currentChunkData.length === 0) {
      return
    }
    const chunkPieces = this.currentChunkData
    const chunkStartedAt = this.chunkStartTime
    this.currentChunkData = []
    const blobType = this.actualMimeType || this.recorder.mimeType || 'audio/webm'
    const rawBlob = new Blob(chunkPieces, { type: blobType })

    const chunkDuration = Date.now() - chunkStartedAt
    const chunk: RecordedChunk = {
      blob: rawBlob,
      durationMs: chunkDuration,
      index: this.chunkIndex,
      createdAt: new Date().toISOString(),
      isFinal,
    }
    this.chunkIndex += 1
    this.chunks.push(chunk)
    if (this.options.onChunk) {
      await this.options.onChunk(chunk)
    }
  }

  private startChunkTimer(): void {
    if (this.chunkTimeout) {
      clearTimeout(this.chunkTimeout)
    }
    this.chunkTimeout = window.setTimeout(() => {
      if (!this.finalStopRequested && this.recorder.state === 'recording') {
        this.recorder.stop()
      }
    }, this.chunkDurationMs)
  }
}
